import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {OnboardingApplicant} from "../../shared/model/onboarding-applicant.model";
import {ReviewState} from "../../review/models/review-state";
import {FacadeService} from "../../shared/service-facade/facade.service";
import {FormArray, FormBuilder, FormControlStatus, FormGroup} from "@angular/forms";
import {MatRadioChange} from "@angular/material/radio";
import {ApplicantsService} from "./applicants.service";
import {FormState} from "src/app/core/interfaces/form-state";
import {ApplicantValidator} from "./applicant-validator";
import {
  BASK_BRAND_ID,
  EMAIL_APPLICANT_ERROR_KEY,
  PHONE_APPLICANT_ERROR_KEY,
  SSN_APPLICANT_ERROR_KEY
} from "src/app/shared/constants";
import {ValidationDetail} from "src/app/shared/model/ValidationDetail";
import {SignupService} from "../service/signup.service";
import {TellUsAboutYourselfData} from "src/app/shared/model/form-data";
import {MatTabGroup} from "@angular/material/tabs";
import {MatDialog} from "@angular/material/dialog";
import {
  ConfirmationDialogComponent,
  ConfirmationDialogData
} from "src/app/shared/components/confirmation-dialog/confirmation-dialog.component";
import {CoreService} from 'src/app/shared/services/core.service';
import {SectionStatusService} from 'src/app/status-tracker/section-status.service';
import {UtilitiesService} from "../../shared/services/utilities.service";
import {take, takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs';
import {
  JointApplicationWarningDialogComponent
} from "../../actions/dialogs/joint-application-warning-dialog/joint-application-warning-dialog.component";
import {ApplicantAccountControllerService} from "leia-api";
import {environment} from "../../../environments/environment";

export interface Account {
  accountNumber?: string;
  productName?: string;
}

//const mParticle = require("@mparticle/web-sdk");

@Component({
  selector: 'app-applicants',
  templateUrl: './applicants.component.html',
  styleUrls: ['./applicants.component.scss']
})
export class ApplicantsComponent implements OnInit, OnDestroy {
  @Input() isInJointMaintenanceFlow: boolean = false;
  @Input() reviewState: ReviewState = ReviewState.Initial;
  @Output() hasCoApplicantEvent = new EventEmitter<boolean>();
  @Output() isFormValidEvent = new EventEmitter<boolean>();
  @ViewChild(MatTabGroup) applicantTabs!: MatTabGroup;

  isMinor: boolean = false;
  multipleApplicant: boolean = false;
  hideMultipleApplicantPrompt: boolean = false;

  overrideApplicantTabLabelMap: Map<number, string> = new Map<number, string>();

  applicants: OnboardingApplicant[] = [];
  reviewStates = ReviewState;
  hideSecondaryApplicant = true;
  destroySubject$: Subject<void> = new Subject();
  accountTypeSelectorState!: ReviewState;
  applicantsForm = new FormGroup({
    applicants: new FormArray([])
  }, {
    validators: [this.applicantValidator.uniqueData()],
    updateOn: "blur"
  });

  validationDetail: ValidationDetail = new ValidationDetail(this.applicantsForm, new Map());

  proceedLinkDetail: string = "Proceed to Co-Applicant >";
  returnLinkDetail: string = "< Return to Primary Applicant";
  accounts: Account[] = [];
  multipleAccounts = false;
  allowJointApplications = false;
  isAuthenticated = this.facadeService.isInAuthenticatedFlow$.getValue()[0];
  sendPrimaryTellUsAboutYourselfData!: () => TellUsAboutYourselfData;

  constructor(
    private facadeService: FacadeService,
    private applicantsService: ApplicantsService,
    private formBuilder: FormBuilder,
    private applicantValidator: ApplicantValidator,
    private signupService: SignupService,
    private dialog: MatDialog,
    private coreService: CoreService,
    private utils: UtilitiesService,
    private accountController: ApplicantAccountControllerService,
  ) {
    if (!this.facadeService.isInJointMaintenanceFlow()) return;

    this.accountController.getAccountProductDetailsBySessionId(this.facadeService.getSessionId()).subscribe(response => {
      response.forEach(product => {
        let account: Account = {
          productName: product.productName,
          accountNumber: product.accountNumber?.slice(product.accountNumber.length - 6)
        };
        this.accounts.push(account);
      });
    });
  }

  get applicantGroups(): FormGroup[] {
    return (<FormArray>this.applicantsForm.get('applicants')).controls as FormGroup[];
  }

  async ngOnInit() {
    this.isMinor = this.facadeService.getIsMinor();

    this.accountTypeSelectorState = this.reviewState;
    this._setupValidationMap()
    this._setupStatusTracker();

    // Listen to posible review data being pushed from verification procedure
    this.facadeService.shoppingCartOnboardingRequest$
      .pipe(
        takeUntil(this.destroySubject$)
      ).subscribe(() => {

      this.loadApplicantsFormGroup();

      if (this.facadeService.getIsMinor()) {
        this.multipleApplicant = true;
        this.hideMultipleApplicantPrompt = true;
        this.hideSecondaryApplicant = false;

        this.overrideApplicantTabLabelMap.set(0, 'Minor Applicant');
        this.overrideApplicantTabLabelMap.set(1, 'Adult Applicant');

        this.proceedLinkDetail = "Proceed to Adult Applicant >";
        this.returnLinkDetail = "< Return to Minor Applicant";
      }

      this.hideMultipleApplicantPrompt = !this.facadeService.allowJointApplications$.getValue();
    });

    if (this.reviewState == ReviewState.Initial) {
      if (!this.coreService.isManualNavigation && !this.isInJointMaintenanceFlow) {
        let isMinor: boolean = false;
        if (this.facadeService.getIsMinor()) isMinor = true;
        const applicantForm = await this.createApplicantFormGroup(true, isMinor);
        (this.applicantsForm.get('applicants') as FormArray).push(applicantForm);

        if (this.facadeService.getIsMinor()) {
          const jointApplicantForm = await this.createApplicantFormGroup(false, false);
          (this.applicantsForm.get('applicants') as FormArray).push(jointApplicantForm);
        }
      }
      if (this.multipleApplicant) {
        this.appendSecondaryApplicant();
      }
      const brandId = Number(this.facadeService.getStorageItem("brandId"));
      //comment mParticle, CDP scripts are live, remove the code later
      // mParticle.default.logPageView(
      //   brandId == BASK_BRAND_ID ? "Step 1 - Begin" : "Step 1 - Apply",
      //   {page: window.location.toString()}
      // );

      this.signupService.addFormToTrackState(this.applicantsForm);

      this.facadeService.isJointApplication$.pipe(take(1)).subscribe(isJointApplication => {
        this.multipleApplicantChange(isJointApplication);
      });
    } else {
      if ((this.applicantsForm.get('applicants') as FormArray).length > 1) {
        this.multipleApplicant = true;
      }
    }
    this.sendPrimaryTellUsAboutYourselfData = this._getPrimaryTellUsAboutYourselfData.bind(this);

    if (this.isInJointMaintenanceFlow) {
      this.multipleApplicant = true;
      this.hideSecondaryApplicant = true;
      this.applicantsForm.markAsUntouched();
    }
  }

  ngOnDestroy(): void {
    this.destroySubject$.next();
  }

  async createApplicantFormGroup(isPrimary: boolean, isMinor: boolean): Promise<FormGroup> {
    const tellUsAboutYourselfForm = await this.applicantsService.createTellUsAboutYourselfFormGroup(isPrimary, isMinor);
    const applicantId = tellUsAboutYourselfForm.value.id;

    let applicantForm: FormGroup;
    if (!isMinor) {
      applicantForm = this.formBuilder.group({
        tellUsAboutYourself: tellUsAboutYourselfForm,
        employmentDetails: this.applicantsService.createEmploymentDetailsFormGroup(applicantId, isPrimary)
      });
    } else {
      applicantForm = this.formBuilder.group({
        tellUsAboutYourself: tellUsAboutYourselfForm
      });
    }

    this.applicantsService.onValidTellUsAboutYourself(tellUsAboutYourselfForm);
    tellUsAboutYourselfForm.statusChanges.subscribe(
      this.onTellUsAboutYourselfFormChanges.bind(this, tellUsAboutYourselfForm)
    );

    if (!isMinor) {
      const employmentDetailsForm = applicantForm.get("employmentDetails") as FormGroup;
      this.applicantsService.onValidEmploymentDetails(employmentDetailsForm);
      employmentDetailsForm.statusChanges.subscribe(
        this.onEmploymentDetailsFormChanges.bind(this, employmentDetailsForm)
      );
    }

    return applicantForm;
  }

  loadApplicantsFormGroup(): void {
    const applicationLogsRequest = this.facadeService.getShoppingCartApplicationLogsRequest();
    const applicantsFormArray = (this.applicantsForm.get('applicants') as FormArray);
    if (!applicationLogsRequest.applicants) return;

    applicationLogsRequest.applicants.forEach(async applicant => {
      let isMinor: boolean = false;
      if (applicant.primary && this.facadeService.getIsMinor())
        isMinor = true;

      let isEmployeeProduct: boolean = false;

      if (this.facadeService.getIsEmployeeProduct()) {
        isEmployeeProduct = true;
      }

      const tellUsAboutYourselfForm = this.applicantsService.loadTellUsAboutYourselfFormGroup(applicant, isMinor, isEmployeeProduct);

      if (this.isAuthenticated) {
        applicantsFormArray.push(
          this.formBuilder.group({
            tellUsAboutYourself: tellUsAboutYourselfForm
          })
        );
      }
      else {
        const employmentDetailsForm = this.applicantsService.loadEmploymentDetailsFormGroup(applicant);

        employmentDetailsForm.markAllAsTouched();

        applicantsFormArray.push(
          this.formBuilder.group({
            tellUsAboutYourself: tellUsAboutYourselfForm,
            employmentDetails: employmentDetailsForm
          })
        );

        employmentDetailsForm.statusChanges.subscribe(
          this.onEmploymentDetailsFormChanges.bind(this, employmentDetailsForm)
        );
      }

      tellUsAboutYourselfForm.markAllAsTouched();

      if (applicantsFormArray.length > 1) {
        this.hideSecondaryApplicant = false;
        //set the joint applicant radio true.
        this.multipleApplicant = true;
      }

      this.hasCoApplicantEvent.emit(this.multipleApplicant);

      tellUsAboutYourselfForm.statusChanges.subscribe(
        this.onTellUsAboutYourselfFormChanges.bind(this, tellUsAboutYourselfForm)
      );

      if (!environment.production) console.log("Applicants form is valid = ", this.applicantsForm.valid);
      this.isFormValidEvent.emit(this.applicantsForm.valid);
    });

    this.signupService.addFormToTrackState(this.applicantsForm);
  }

  onTellUsAboutYourselfFormChanges(form: FormGroup, formChanges: string): Promise<void> {
    if (formChanges === FormState.VALID) {
      return this.applicantsService.onValidTellUsAboutYourself(form);
    }

    return new Promise<void>((resolve, reject) => {
      resolve();
    })
  }

  onEmploymentDetailsFormChanges(form: FormGroup, formChanges: string): Promise<void> {
    if (formChanges === FormState.VALID) {
      return this.applicantsService.onValidEmploymentDetails(form);
    }

    return new Promise<void>((resolve, reject) => {
      resolve();
    })
  }

  getTellUsAboutYourselfFormGroup(applicantFormGroup: FormGroup): FormGroup {
    return applicantFormGroup.get('tellUsAboutYourself') as FormGroup;
  }

  getEmploymentDetailsFormGroup(applicantFormGroup: FormGroup): FormGroup {
    return applicantFormGroup.get('employmentDetails') as FormGroup;
  }

  getApplicantType(formGroup: FormGroup, index: number): any {
    let applicantType = (formGroup.get("tellUsAboutYourself") as FormGroup).get('applicantType')?.value;

    if (this.overrideApplicantTabLabelMap.get(index) === undefined) return applicantType;

    return this.overrideApplicantTabLabelMap.get(index);
  }

  multipleApplicantChange(isJointApplication: boolean): void {
    if (isJointApplication) {
      this.appendSecondaryApplicant();
    }

    this.hasCoApplicantEvent.emit(isJointApplication);
  }

  appendSecondaryApplicant(): void {
    const formArray = this.applicantsForm.get("applicants") as FormArray;
    if (formArray.length === 1) {
      this.createApplicantFormGroup(false, false).then(form => {
        formArray.push(form);
      });
    }

    this.hideSecondaryApplicant = false;
    this.applicantsService.unmarkSecondaryApplicantForRemoval();
  }

  removeSecondaryApplicant(): void {
    this.hideSecondaryApplicant = true;
    this.applicantsService.markSecondaryApplicantForRemoval();
    //Fix to remove the appended applicant on application type selection
    this.applicantsService.removeSecondaryApplicantIfNecessary(this.applicantsForm.get('applicants') as FormArray);
  }

  removeSecondaryApplicantFromApplication(): void {
    const dialogData: ConfirmationDialogData = {
      title: "Confirm action",
      message: "Are you sure?"
    }
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      maxWidth: "300px",
      data: dialogData
    });

    dialogRef.afterClosed().subscribe(isActionConfirmed => {
      if (!isActionConfirmed) {
        return;
      }

      this.hideSecondaryApplicant = true;
      this.multipleApplicant = false;
      this.facadeService.markApplicationAsIndividual();
      this.applicantsService.markSecondaryApplicantForRemoval();
      this.applicantsService.removeSecondaryApplicantIfNecessary(this.applicantsForm.get('applicants') as FormArray);
      this.hasCoApplicantEvent.emit(false);
    });
  }

  isPrimaryHasSecondary(formGroup: FormGroup): boolean {
    return formGroup.value.tellUsAboutYourself.isPrimary &&
      this.applicantsForm.value.applicants.length > 1;
  }

  isSecondary(formGroup: FormGroup): boolean {
    return !formGroup.value.tellUsAboutYourself.isPrimary;
  }

  selectSecondaryTab(): void {
    this.applicantTabs.selectedIndex = 1;
    this.utils.scrollToElement("joint-tab-group");
  }

  selectPrimaryTab(): void {
    this.applicantTabs.selectedIndex = 0;
    this.utils.scrollToElement("joint-tab-group");
  }

  private _getPrimaryTellUsAboutYourselfData(): TellUsAboutYourselfData {
    return this.applicantsForm.value.applicants.find((applicant: any) =>
      applicant.tellUsAboutYourself.isPrimary
    )!.tellUsAboutYourself;
  }

  private _getTellUsAboutYourselfFormState(): { status: FormControlStatus[], dirty: boolean } {
    const state: {
      status: FormControlStatus[],
      dirty: boolean[]
    } = {
      status: [],
      dirty: []
    };

    (this.applicantsForm.controls.applicants as FormArray).controls.forEach(applicantsForm => {
      const {tellUsAboutYourself} = (applicantsForm as FormGroup).controls;
      state.status.push(tellUsAboutYourself.status);
      state.dirty.push(tellUsAboutYourself.dirty);
    });

    return {
      status: state.status,
      dirty: state.dirty.includes(true)
    };
  }

  private _setupStatusTracker(): void {
    this.applicantsForm.statusChanges.subscribe(state => {
      const validTellUsAboutYourself: string[] = [];
      const validEmploymentDetails: string[] = [];
      let dirtyTellUsAboutYourself = false;
      let dirtyEmploymentDetails = false;

      (this.applicantsForm.controls.applicants as FormArray).controls.forEach(applicantForm => {
        const {tellUsAboutYourself, employmentDetails} = (applicantForm as FormGroup).controls;

        validTellUsAboutYourself.push(tellUsAboutYourself.status);
        if (!dirtyTellUsAboutYourself) {
          dirtyTellUsAboutYourself = tellUsAboutYourself.dirty;
        }

        if (!this.facadeService.getIsMinor() && !this.isAuthenticated) {
          validEmploymentDetails.push(employmentDetails.status);
          if (!dirtyEmploymentDetails) {
            dirtyEmploymentDetails = employmentDetails.dirty;
          }
        }
      });

      this.signupService.validationsChange.emit(this.validationDetail);
      this.applicantsService.sendDuplicatedDataErrors(this.applicantsForm.errors!);
    });
  }

  private _setupValidationMap(): void {
    const labelMap = new Map<string, string>();
    labelMap.set(SSN_APPLICANT_ERROR_KEY, "Applicants cannot share the same Social Security Number");
    labelMap.set(EMAIL_APPLICANT_ERROR_KEY, "Applicants cannot share the same Email");
    labelMap.set(PHONE_APPLICANT_ERROR_KEY, "Applicants cannot share the same Phone Number");
    this.validationDetail = new ValidationDetail(this.applicantsForm, labelMap);
  }
}
